import { Menu, MenuItem, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { DatumValue } from "@nivo/core";
import { ReactComponent as Download01Icon } from "components/Icons/download-01.svg";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import { toJpeg, toPng } from "html-to-image";
import { unparse } from "papaparse";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    tooltipTitle: {
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "24px",
      color: theme.gray800,
    },
    icon: {
      width: "24px",
      height: "24px",
      cursor: "pointer",
      color: theme.gray800,

      "&:hover": {
        color: theme.gray500,
      },
    },
  }),
);

interface Props {
  imgContainerId: string;
  chartName?: string;
  data?: { date: DatumValue | string; value: DatumValue | number }[][];
}

export const DownloadChart = ({ imgContainerId, chartName, data }: Props) => {
  const { anchor, isMenuOpen, handleMenuClose, handleMenuOpen } =
    useContextMenu();
  const classes = useStyles();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const fileName = chartName ? `chart-${chartName.toLowerCase()}` : "chart";

  const download = (filename: string, href: string) => {
    const link = document.createElement("a");
    link.download = filename;
    link.href = href;
    link.click();

    handleMenuClose();
  };

  const exportAsImage = (type: "png" | "jpg") => {
    const node = document.getElementById(imgContainerId);
    if (!node) return;

    const exportImage = type === "png" ? toPng : toJpeg;

    exportImage(node as HTMLElement, {
      cacheBust: true,
      backgroundColor: type === "png" ? undefined : "white",
      filter: (node: HTMLElement) => {
        const excludedClasses = "MuiTabScrollButton-root";

        return !node?.classList?.contains(excludedClasses);
      },
    })
      .then(function (dataUrl) {
        download(`${fileName}.${type}`, dataUrl);
      })
      .catch(function (error) {
        notify(message({ id: "exportChartError" }));
      });
  };

  const exportAsCsv = () => {
    if (!data) return notify(message({ id: "exportChartError" }));

    try {
      const output = data
        .map((d) =>
          unparse(d, {
            columns: ["date", "value", "dataSetName"],
          }),
        )
        .join("\n");

      const blob = new Blob([output]);
      download(`${fileName}.csv`, URL.createObjectURL(blob));
    } catch {
      notify(message({ id: "exportChartError" }));
    }
  };

  return (
    <>
      <Tooltip
        text={
          <Typography className={classes.tooltipTitle}>
            {message({ id: "exportChart" })}
          </Typography>
        }
      >
        <Download01Icon
          data-test-id="export-chart"
          className={classes.icon}
          onClick={handleMenuOpen}
        />
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={isMenuOpen}
        onClose={handleMenuClose}
        transformOrigin={{
          horizontal: "center",
          vertical: "top",
        }}
        anchorOrigin={{
          horizontal: "center",
          vertical: "bottom",
        }}
      >
        <MenuItem
          onClick={() => exportAsImage("png")}
          data-test-id="export-chart-as-png"
        >
          {message({ id: "exportAsPNG" })}
        </MenuItem>
        <MenuItem
          onClick={() => exportAsImage("jpg")}
          data-test-id="export-chart-as-jpg"
        >
          {message({ id: "exportAsJPG" })}
        </MenuItem>
        <MenuItem onClick={exportAsCsv} data-test-id="export-chart-as-csv">
          {message({ id: "exportAsCSV" })}
        </MenuItem>
      </Menu>
    </>
  );
};
